import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import SiteFooter from "../../components/SiteFooter";
import { ArrowRight, ArrowLeft } from "lucide-react";
import BrandLogo from "../../components/BrandLogo";

const STEPS = [
  { key: "company", title: "Tell us about your brand" },
  { key: "category", title: "Your category & audience" },
  { key: "fit", title: "Who you want to work with" },
];

const SPORTS = ["Football", "Basketball", "Athletics", "MMA / Combat", "Cycling", "Rugby", "Cricket", "Tennis", "Golf", "Esports", "Swimming", "Padel"];
const REGIONS = ["UK", "Europe", "Middle East", "USA", "Asia", "Africa", "Australia", "Global"];
const BUDGETS = ["£250-£1K", "£1K-£5K", "£5K-£15K", "£15K-£50K", "£50K+"];

export default function BrandOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    company_name: user?.full_name || "", category: "", website: "", headquarters: "",
    budget_range: "", target_sports: [], target_regions: [], audience_focus: "", company_bio: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.account_type !== "brand") {
      navigate("/onboarding");
      return;
    }
    if (user.onboarded) navigate("/app/brand");
  }, [user, navigate]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (k, v) => upd(k, form[k].includes(v) ? form[k].filter(x => x !== v) : [...form[k], v]);

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/brand/onboarding/complete", form);
      await refresh();
      navigate("/app/brand");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-bg text-white flex flex-col">
      <div className="px-6 md:px-12 py-6 border-b border-line flex justify-between items-center">
        <BrandLogo variant="wordmark" size={28} />
        <div className="label-eyebrow">Step {step + 1} / {STEPS.length}</div>
      </div>
      <div className="h-1 w-full bg-line">
        <div className="h-1 bg-volt transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-12 py-12">
        <div className="label-eyebrow">For brands</div>
        <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-3" data-testid="brand-onboarding-title">{STEPS[step].title}</h1>

        <div className="mt-10 space-y-6">
          {step === 0 && (
            <>
              <Field label="Company / brand name *"><Input value={form.company_name} onChange={v => upd("company_name", v)} testid="brand-company-name" /></Field>
              <Field label="Website"><Input value={form.website} onChange={v => upd("website", v)} placeholder="https://acme.com" testid="brand-website" /></Field>
              <Field label="Headquarters"><Input value={form.headquarters} onChange={v => upd("headquarters", v)} placeholder="London, UK" testid="brand-hq" /></Field>
              <Field label="Short company bio">
                <textarea rows={4} value={form.company_bio} onChange={e => upd("company_bio", e.target.value)} data-testid="brand-bio"
                  placeholder="What does your brand stand for? Who are you trying to reach?"
                  className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white resize-none" />
              </Field>
            </>
          )}
          {step === 1 && (
            <>
              <Field label="Category *"><Input value={form.category} onChange={v => upd("category", v)} placeholder="Apparel, Nutrition, Fintech, Recovery…" testid="brand-category" /></Field>
              <Field label="Typical campaign budget">
                <div className="flex flex-wrap gap-2">
                  {BUDGETS.map(b => (
                    <button key={b} type="button" onClick={() => upd("budget_range", b)} data-testid={`brand-budget-${b}`}
                      className={`px-3 py-2 border text-xs uppercase tracking-widest ${form.budget_range === b ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>{b}</button>
                  ))}
                </div>
              </Field>
              <Field label="Audience focus">
                <textarea rows={3} value={form.audience_focus} onChange={e => upd("audience_focus", e.target.value)} data-testid="brand-audience-focus"
                  placeholder="e.g. 18-34 UK male sports fans interested in performance gear"
                  className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white resize-none" />
              </Field>
            </>
          )}
          {step === 2 && (
            <>
              <Field label="Target sports">
                <div className="flex flex-wrap gap-2">
                  {SPORTS.map(s => (
                    <button key={s} type="button" onClick={() => toggle("target_sports", s)} data-testid={`brand-sport-${s}`}
                      className={`px-3 py-2 border text-xs uppercase tracking-widest ${form.target_sports.includes(s) ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>{s}</button>
                  ))}
                </div>
              </Field>
              <Field label="Target regions">
                <div className="flex flex-wrap gap-2">
                  {REGIONS.map(r => (
                    <button key={r} type="button" onClick={() => toggle("target_regions", r)} data-testid={`brand-region-${r}`}
                      className={`px-3 py-2 border text-xs uppercase tracking-widest ${form.target_regions.includes(r) ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>{r}</button>
                  ))}
                </div>
              </Field>
            </>
          )}
        </div>

        <div className="mt-12 flex items-center justify-between">
          <button onClick={prev} disabled={step === 0} className="btn-ghost-line inline-flex items-center gap-2 disabled:opacity-30" data-testid="brand-onb-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} disabled={step === 0 && !form.company_name} className="btn-volt inline-flex items-center gap-2 disabled:opacity-40" data-testid="brand-onb-next">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={finish} disabled={saving || !form.company_name || !form.category} className="btn-volt inline-flex items-center gap-2 disabled:opacity-40" data-testid="brand-onb-finish">
              {saving ? "Saving…" : "Enter brand workspace"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
function Input({ value, onChange, placeholder = "", testid }) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />;
}
